import { Select, Switch } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import classNames from 'classnames';
import { subDays, subYears } from 'date-fns';
import { useState } from 'react';
import DateRangePickerDropdown from '../../../components/DateRangePickerDropdown';
import { useUserStore } from '../../../components/userStore';
import constants from '../../../constants';
import ClosedStatusBreakdown from './components/ClosedStatusBreakdown';
import KPI from './components/KPI';
import TopReferringOrgs from './components/TopReferringOrgs';
import TopSKUs from './components/TopSKUs';
import TopServiceCategories from './components/TopServiceCategories';
import ViewDataModal from './components/ViewDataModal';

const isSameFilter = (a, b) =>
	a.length === b.length && a.every((s) => b.includes(s));

const ReportingDashboard = () => {
	const [startDate, setStartDate] = useState(subDays(new Date(), 30));
	const [endDate, setEndDate] = useState(new Date());
	const [view, setView] = useState('incoming');
	const [showAllHub, setShowAllHub] = useState(false);
	const [isViewDataModalOpen, setIsViewDataModalOpen] = useState(false);
	const [filteredStatuses, setFilteredStatuses] = useState([]);
	const { user } = useUserStore();
	const hubOwner = user?.coord_agency?.hubs.find(
		(hub) => hub.hub_owner_agency_id === user.coord_agency_id,
	);

	const { data } = useQuery({
		queryKey: [
			'reporting-dashboard',
			user.coord_agency.id,
			startDate,
			endDate,
			showAllHub,
		],
		queryFn: async () => {
			const options = {
				startDate,
				endDate,
			};
			if (showAllHub) {
				options.hubId = hubOwner.id;
			}
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency.id}/reporting-data`,
				{
					params: {
						options,
					},
				},
			);
			return data;
		},
	});

	const handleDateRangeChange = ({ startDate, endDate }) => {
		setStartDate(startDate);
		setEndDate(endDate);
	};

	const viewData = data?.filter((row) => {
		if (showAllHub) {
			return true;
		} else {
			if (view === 'incoming') {
				return (
					row.rec_agency_id === user.coord_agency.id &&
					row.request_status !== 'incomplete'
				);
			} else {
				return row.ref_agency_id === user.coord_agency.id;
			}
		}
	});

	return (
		<>
			<div
				className={classNames('grid gap-4 pb-4', {
					'grid-cols-5': view === 'incoming',
					'grid-cols-6': view === 'outgoing',
				})}
			>
				<div className="flex items-center justify-between col-span-full">
					<div>
						<Select
							className="select select-sm select-bordered"
							value={view}
							onChange={({ target }) => setView(target.value)}
						>
							<option value="incoming">Incoming Activity</option>
							<option value="outgoing">Outgoing Activity</option>
						</Select>
						{hubOwner && (
							<div className="inline-block ml-4 form-control">
								<label className="gap-2 cursor-pointer label">
									<span className="label-text">Show All Hub Data</span>
									<Switch
										checked={showAllHub}
										onChange={setShowAllHub}
										className="toggle toggle-primary"
									></Switch>
								</label>
							</div>
						)}
						{filteredStatuses.length > 0 && (
							<button
								className="ml-2 btn btn-sm btn-ghost"
								onClick={() => setFilteredStatuses([])}
							>
								Show All
							</button>
						)}
					</div>
					<div className="flex flex-row gap-4">
						<button
							className="btn btn-sm btn-bordered"
							onClick={() => setIsViewDataModalOpen(true)}
							disabled={!viewData?.length}
						>
							View Data
						</button>
						<DateRangePickerDropdown
							startDate={startDate}
							endDate={endDate}
							onRangeChosen={handleDateRangeChange}
							minDate={subYears(new Date(), 2)}
						/>
					</div>
				</div>
				{view === 'outgoing' && (
					<KPI
						title="Incomplete"
						data={viewData?.filter(
							(row) => 'incomplete' === row.request_status,
						)}
						onClick={() => setFilteredStatuses(['incomplete'])}
						selected={isSameFilter(filteredStatuses, ['incomplete'])}
					/>
				)}
				<KPI
					title="New"
					data={viewData?.filter((row) => 'new' === row.request_status)}
					onClick={() => setFilteredStatuses(['new'])}
					selected={isSameFilter(filteredStatuses, ['new'])}
				/>
				<KPI
					title="Open"
					data={viewData?.filter((row) =>
						constants.openStatuses.includes(row.request_status),
					)}
					onClick={() => setFilteredStatuses(constants.openStatuses)}
					selected={isSameFilter(filteredStatuses, constants.openStatuses)}
				/>
				<KPI
					title="Waitlist"
					data={viewData?.filter((row) => 'waitlist' === row.request_status)}
					onClick={() => setFilteredStatuses(['waitlist'])}
					selected={isSameFilter(filteredStatuses, ['waitlist'])}
				/>
				<KPI
					title="On Hold"
					data={viewData?.filter((row) => 'onhold' === row.request_status)}
					onClick={() => setFilteredStatuses(['onhold'])}
					selected={isSameFilter(filteredStatuses, ['onhold'])}
				/>
				<KPI
					title="Closed"
					data={viewData?.filter((row) =>
						constants.closedStatuses.includes(row.request_status),
					)}
					onClick={() => setFilteredStatuses(constants.closedStatuses)}
					selected={isSameFilter(filteredStatuses, constants.closedStatuses)}
				/>
				<div className="p-4 bg-white border col-span-full rounded-xl">
					<TopReferringOrgs
						data={viewData}
						outgoing={view === 'outgoing'}
						statuses={filteredStatuses}
					/>
				</div>
				{isSameFilter(filteredStatuses, constants.closedStatuses) && (
					<div className="p-4 bg-white border col-span-full rounded-xl">
						<ClosedStatusBreakdown data={viewData} />
					</div>
				)}
				<div className="p-4 bg-white border col-span-full rounded-xl">
					<TopServiceCategories data={viewData} statuses={filteredStatuses} />
				</div>
				<div className="p-4 bg-white border col-span-full rounded-xl">
					<TopSKUs data={viewData} statuses={filteredStatuses} />
				</div>
			</div>
			<ViewDataModal
				isOpen={isViewDataModalOpen}
				data={viewData}
				onClose={() => setIsViewDataModalOpen(false)}
			/>
		</>
	);
};

export default ReportingDashboard;
